<template>
  <div class="group/comment flex gap-2.5">
    <div class="flex shrink-0 flex-col items-center">
      <CommentAvatar
        :user-id="comment.author.id"
        :first-name="comment.author.firstName"
        :last-name="comment.author.lastName"
      />
      <!-- Bleeds into the gap below so the line reaches the next avatar. -->
      <div v-if="connected" class="-mb-3 mt-1 w-px flex-1 bg-obsidian-200"></div>
    </div>
    <div class="min-w-0 flex-1">
      <div class="flex items-center gap-2">
        <span class="truncate text-sm font-semibold leading-tight text-obsidian-900">
          {{ comment.author.firstName }} {{ comment.author.lastName }}
        </span>
        <span class="shrink-0 text-xs leading-tight text-obsidian-500" :title="absoluteDate">
          {{ isEdited ? `${relativeDate} · ${editedLabel}` : relativeDate }}
        </span>
        <div v-if="!editing" class="ml-auto flex shrink-0 items-center gap-0.5">
          <CVButton
            type="button"
            variant="ghost"
            size="iconXs"
            class="text-obsidian-400 opacity-0 hover:text-obsidian-800 focus-visible:opacity-100 group-hover/comment:opacity-100"
            :aria-label="t('addReaction')"
            :title="t('addReaction')"
            @click="handleOpenReactions"
          >
            <SmilePlusIcon class="size-4" />
          </CVButton>
          <CVButton
            v-if="canEdit"
            type="button"
            variant="ghost"
            size="iconXs"
            class="text-obsidian-400 opacity-0 hover:text-obsidian-800 focus-visible:opacity-100 group-hover/comment:opacity-100"
            :aria-label="editLabel"
            :title="editLabel"
            @click="editing = true"
          >
            <PencilIcon class="size-4" />
          </CVButton>
          <CVButton
            v-if="canEdit"
            type="button"
            variant="ghost"
            size="iconXs"
            class="text-obsidian-400 opacity-0 hover:text-red-600 focus-visible:opacity-100 group-hover/comment:opacity-100"
            :aria-label="deleteLabel"
            :title="deleteLabel"
            @click="emit('delete', comment.id)"
          >
            <TrashIcon class="size-4" />
          </CVButton>
          <slot name="actions" />
        </div>
      </div>
      <CommentEditor
        v-if="!editing"
        class="mt-1 text-obsidian-800"
        :entity-type="entityType"
        :entity-id="entityId"
        :model-value="comment.body"
        :editable="false"
        :placeholder="placeholder"
        :no-matches="noMatches"
      />
      <CommentEditor
        v-else
        ref="editEditor"
        bare
        autofocus
        class="mt-1 text-obsidian-800"
        :entity-type="entityType"
        :entity-id="entityId"
        :model-value="comment.body"
        :placeholder="placeholder"
        :no-matches="noMatches"
        :show-submit="true"
        :is-submitting="saving"
        :submit-icon="CheckIcon"
        :submit-label="saveLabel"
        :show-cancel="true"
        :cancel-label="cancelLabel"
        @submit="handleSave"
        @cancel="editing = false"
      />
      <CommentReactions
        v-if="!editing"
        ref="reactionsRef"
        :reactions="comment.reactions ?? []"
        :me-id="meId"
        @toggle="emit('react', { commentId: comment.id, emoji: $event })"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { CheckIcon, PencilIcon, SmilePlusIcon, TrashIcon } from 'lucide-vue-next';
import CVButton from '@/components/buttons/CVButton.vue';
import CommentAvatar from './CommentAvatar.vue';
import CommentEditor from './CommentEditor.vue';
import CommentReactions from './CommentReactions.vue';
import type { CommentThread, CommentableEntityType, TipTapNode } from '@/api/comments.service';
import { formatDate } from '@/hooks/use-formatted-date';
import { i18n } from '@/i18n';

const props = withDefaults(
  defineProps<{
    cancelLabel: string;
    canEdit: boolean;
    comment: CommentThread;
    /** Draws a timeline rail from this avatar down to the next one. */
    connected?: boolean;
    deleteLabel: string;
    editLabel: string;
    editedLabel: string;
    entityId: string;
    entityType: CommentableEntityType;
    justNowLabel: string;
    meId?: string | null;
    noMatches: string;
    placeholder: string;
    saveLabel: string;
    onSave: (payload: { body: TipTapNode; commentId: string }) => Promise<void>;
  }>(),
  { connected: false, meId: null },
);

const emit = defineEmits<{
  delete: [commentId: string];
  react: [payload: { commentId: string; emoji: string }];
}>();

const { t } = useI18n();
const editing = ref(false);
const saving = ref(false);
const editEditor = ref<{ isEmpty: () => boolean; getJSON: () => TipTapNode } | null>(null);
const reactionsRef = ref<{
  togglePicker: (event?: Event) => void;
} | null>(null);

const absoluteDate = computed(() => formatDate(props.comment.createdDate, DateTime.DATETIME_MED));

const relativeDate = computed(() => {
  const created = DateTime.fromISO(props.comment.createdDate);
  if (Math.abs(created.diffNow('seconds').seconds) < 60) return props.justNowLabel;
  return created.toRelative({ locale: toValue(i18n.global.locale) }) ?? absoluteDate.value;
});

const isEdited = computed(() => {
  if (!props.comment.updatedDate || !props.comment.createdDate) return false;
  return (
    new Date(props.comment.updatedDate).getTime() - new Date(props.comment.createdDate).getTime() >
    1000
  );
});

const handleOpenReactions = (event: Event) => {
  reactionsRef.value?.togglePicker(event);
};

const handleSave = async () => {
  if (!editEditor.value || editEditor.value.isEmpty() || saving.value) return;
  saving.value = true;
  try {
    await props.onSave({ commentId: props.comment.id, body: editEditor.value.getJSON() });
    editing.value = false;
  } finally {
    saving.value = false;
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "addReaction": "Add reaction"
  },
  "de": {
    "addReaction": "Reaktion hinzufügen"
  },
  "es": {
    "addReaction": "Añadir reacción"
  }
}
</i18n>
